import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/shared/StatsCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import { Users, FileText, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { agentAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AgentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    assigned_clients: 0,
    pending_review: 0,
    in_progress: 0,
    completed: 0
  });
  const [assignedClients, setAssignedClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch agent statistics
      const statsData = await agentAPI.getAgentStats();
      setStats(statsData);
      
      // Fetch assigned clients
      const clientsData = await agentAPI.getAgentClients();
      setAssignedClients(clientsData.slice(0, 5)); // Show top 5
      
      // Fetch tasks
      const tasksData = await agentAPI.getAgentTasks();
      setTasks(tasksData.slice(0, 5)); // Show top 5
      
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Error fetching agent data:', error);
      if (!isRefresh) {
        toast.error('Failed to load agent dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      fetchData(true);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user, fetchData]);

  const handleManualRefresh = () => {
    fetchData(true);
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Agent Dashboard" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout role={user.role} title="Agent Dashboard" user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Welcome back, {user.first_name || user.email}!</h2>
            <p className="mt-1 text-muted-foreground">Here's your workload overview</p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleManualRefresh}
              disabled={refreshing}
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Assigned Clients"
            value={stats.assigned_clients.toString()}
            icon={Users}
            iconBgColor="bg-primary"
          />
          <StatsCard
            title="Pending Review"
            value={stats.pending_review.toString()}
            icon={Clock}
            iconBgColor="bg-warning"
          />
          <StatsCard
            title="In Progress"
            value={stats.in_progress.toString()}
            icon={FileText}
            iconBgColor="bg-info"
          />
          <StatsCard
            title="Completed"
            value={stats.completed.toString()}
            icon={CheckCircle}
            iconBgColor="bg-success"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Assigned Clients */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Assigned Clients</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/agent/clients')}
                >
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assignedClients.length > 0 ? (
                  assignedClients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/agent/clients/${client.id}`)}
                    >
                      <div>
                        <p className="font-medium text-foreground">{client.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Updated {formatDate(client.last_updated)}
                        </p>
                      </div>
                      <StatusBadge status={client.filing_status} />
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">No clients assigned yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Today's Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tasks.length > 0 ? (
                  tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/agent/filings/${task.filing_id}`)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{task.description}</p>
                        <p className="text-xs text-muted-foreground">
                          Updated: {formatDate(task.due)} • Priority: {task.priority}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">No tasks at the moment</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
